import { Head, Link } from '@inertiajs/react';
import { PublicLayout } from '@/components/public/PublicLayout';
import { TRUST_ITEMS } from '@/components/public/trust-items';
import { motion } from '@/components/public/PublicMotion';
import { fadeInUp, staggerContainer, scaleIn } from '@/hooks/use-scroll-animation';
import { PublicDemoLink } from '@/components/public/PublicDemoLink';
import { Button } from '@/components/ui/button';
import { VideoEmbed } from '@/components/public/VideoEmbed';

export interface VideoLandingConfig {
  pageTitle: string;
  badge?: string;
  headline: string;
  lead: string;
  videoProvider: 'youtube' | 'loom';
  videoId: string;
  videoTitle?: string;
  supportCopy: string;
  ctaLabel: string;
  ctaLocation: string;
  calendarUrl?: string;
}

interface VideoLandingPageProps {
  config: VideoLandingConfig;
}

export function VideoLandingPage({ config }: VideoLandingPageProps) {
  return (
    <PublicLayout>
      <Head title={config.pageTitle}>
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      {/* SEZIONE HERO */}
      <section className="pt-16 md:pt-20 pb-12 md:pb-16 bg-blue-lighter/50">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl mx-auto text-center"
          >
            {config.badge && (
              <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
                <span className="rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary/80 inline-block mb-4">
                  {config.badge}
                </span>
              </motion.div>
            )}

            <motion.h1
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="font-display text-4xl md:text-5xl font-bold text-primary mb-3"
            >
              {config.headline}
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-lg text-primary-dark"
            >
              <span dangerouslySetInnerHTML={{ __html: config.lead }} />
            </motion.p>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto mt-8 md:mt-10"
          >
            <VideoEmbed
              provider={config.videoProvider}
              videoId={config.videoId}
              title={config.videoTitle}
            />
            <p className="text-muted-foreground text-center mt-4 max-w-2xl mx-auto text-sm">
              {config.supportCopy}
            </p>
          </motion.div>
        </div>
      </section>

      {/* SEZIONE CTA */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold text-primary mb-4">
                Pronto a scoprire come Emovia ti può aiutare?
              </h2>
              <p className="text-lg text-primary-dark leading-relaxed mb-8">
                Prenota con me una sessione di 30&apos;. Nessun impegno.
              </p>
              <Button size="lg" asChild className="text-base px-8 h-12">
                {config.calendarUrl ? (
                  <Link href={config.calendarUrl}>{config.ctaLabel}</Link>
                ) : (
                  <PublicDemoLink location={config.ctaLocation}>{config.ctaLabel}</PublicDemoLink>
                )}
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-8 bg-primary">
        <div className="container">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {TRUST_ITEMS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-primary-foreground/80" />
                <span className="text-primary-foreground/90 text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
